/**
 * ENT-5889: typed client boundary for runtime advisories (php2shell and later).
 *
 * Every verdict, badge, count and sentence comes from the server (get_advisory_page,
 * get_vendor_advisory_page, get_advisory_console, get_listing_runtime_technologies and the
 * advisory-email / public-runtime-check Edge Functions). This module only calls those boundaries,
 * narrows their payloads, maps the canonical `advisory_*` refusals to readable messages, and
 * serialises returned rows to CSV. It never derives support_state or copy.
 */
// @canonical-document-url-exception downloadCsv builds a local object URL for a CSV generated in the browser from server rows; it is never a storage or document URL.
import { supabase } from '@/integrations/supabase/client';
import { formatDateTime } from '@/lib/dateTimeFormat';
import { buildFunctionInvokeError } from '@/lib/functionInvokeErrors';
import { invokeProtectedFunction } from '@/lib/protectedFunctionAuth';

export type RuntimeSupportState = 'patched' | 'below_fix' | 'eol' | 'unknown';

export interface RuntimeAdvisoryHeader {
  advisory_id: string;
  product: string;
  product_label: string;
  title: string;
  short_name: string;
  summary: string;
  impact?: string;
  cve_ids: string[];
  cve_label: string;
  published_at: string;
  exploited: boolean;
  source_url: string | null;
  status: string;
  fixed_in: string[];
}

export interface AdvisoryRow {
  finding_id: string;
  organization_id: string;
  supplier_domain_id: string;
  marketplace_listing_id: string;
  supplier_id: string | null;
  is_own: boolean;
  supplier_name: string | null;
  /** Canonical Marketplace projection for a supplier row; never raw discovery evidence. */
  supplier_logo_url: string | null;
  host: string;
  discovered: boolean;
  product: string;
  observed_version: string | null;
  support_state: RuntimeSupportState;
  state_reason: string | null;
  badge: string;
  severity: 'medium' | 'high' | null;
  reference_version: string | null;
  eol_date: string | null;
  last_seen_at: string | null;
  last_checked_at: string | null;
  /** ENT-6292: first observation of the current run of fixed versions; null unless patched. */
  fix_observed_at: string | null;
  /** ENT-6292: what preceded that run; null unless patched. */
  fix_context: RuntimeFixContext | null;
  customer_summary: string;
  deferred_by: string | null;
  deferred_by_name: string | null;
  deferred_at: string | null;
  poam_id: string | null;
  poam_status: string | null;
  poam_due_date: string | null;
}

/**
 * ENT-6292: what preceded a patched host's current run of fixed versions: a below-fix or
 * end-of-life version (`after_exposure`), nothing (`first_check`), or an observation that could not
 * be verified (`after_unverified`).
 */
export type RuntimeFixContext = 'after_exposure' | 'first_check' | 'after_unverified';

export interface AdvisoryGroupCounts {
  exposed_hosts: number;
  own_exposed_hosts: number;
  exposed_suppliers: number;
  unsupported_hosts: number;
  patched_hosts: number;
  cannot_verify_hosts: number;
  open_actions: number;
}

export interface AdvisoryGroup {
  organization_id: string;
  organization_name: string;
  access: 'act' | 'view' | 'view_suppliers';
  via_msp: boolean;
  needs_domain_confirmation: boolean;
  rows: AdvisoryRow[];
  counts: AdvisoryGroupCounts;
  last_checked_at: string | null;
}

export interface AdvisoryPage {
  advisory: RuntimeAdvisoryHeader;
  viewer: { is_msp_view: boolean };
  groups: AdvisoryGroup[];
}

export interface VendorAdvisoryRow {
  finding_id: string;
  supplier_domain_id: string;
  host: string;
  /** The listing's primary website (never dismissable as "not ours"); absent on older payloads. */
  is_primary?: boolean;
  discovered: boolean;
  product: string;
  observed_version: string | null;
  support_state: RuntimeSupportState;
  state_reason: string | null;
  badge: string;
  severity: 'medium' | 'high' | null;
  reference_version: string | null;
  eol_date: string | null;
  last_seen_at: string | null;
  last_checked_at: string | null;
  /** ENT-6292: first observation of the current run of fixed versions; null unless patched. */
  fix_observed_at: string | null;
  /** ENT-6292: what preceded that run; null unless patched. */
  fix_context: RuntimeFixContext | null;
  customer_summary: string;
  poam_id: string | null;
  poam_status: string | null;
  poam_due_date: string | null;
  /** The server-resolved supplier share flag; vendor-team-only users may open only shared Actions. */
  poam_shared_with_supplier?: boolean;
}

export interface VendorAdvisoryListing {
  vendor_profile_id: string;
  marketplace_listing_id: string;
  claimant_organization_id: string | null; // @canonical-org-exception server-resolved get_vendor_advisory_page payload field, not an org settings binding
  can_act: boolean;
  /** Org act on the claimant organisation: the create_poam_from_findings write boundary. */
  can_create_action: boolean;
  listing_name: string;
  watching_customers: number;
  rows: VendorAdvisoryRow[];
}

export interface VendorAdvisoryPage {
  advisory: RuntimeAdvisoryHeader;
  listings: VendorAdvisoryListing[];
}

export interface AdvisorySelection {
  organization_id: string;
  finding_id: string;
  /** "Create action anyway" on a cannot-verify (unknown) host; the server refuses it otherwise. */
  unverified?: boolean;
}

export interface CreatePoamFromFindingsResult {
  created: Array<{
    poam_id: string;
    organization_id: string;
    supplier_name: string | null;
    is_own: boolean;
    title: string;
    due_date: string;
    finding_ids: string[];
  }>;
  attached: Array<{ poam_id: string; organization_id: string; finding_ids: string[] }>;
  unchanged: Array<{ organization_id: string; finding_id: string; poam_id: string }>;
  deferred: number;
}

export type ConsoleRecipientStatus = 'not_emailed' | 'sent' | 'delivered' | 'opened' | 'failed';
export type ConsoleOrgType = 'direct' | 'msp_customer' | 'msp' | 'supplier';

export interface ConsoleRecipient {
  user_id: string;
  email: string | null;
  audience: 'customer' | 'supplier';
  rung: string;
  side: 'customer' | 'msp';
  hosts: number;
  also_covers: number;
  status: ConsoleRecipientStatus;
  sent_at: string | null;
  delivered_at: string | null;
  failed_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  visited_at: string | null;
  sends: number;
  actions_created: number;
  /** Unavailable: absence of provider opens cannot establish unread behavior. */
  unopened: null;
  /** runtime_advisories state of this recipient's organisation; flag-off recipients are never sent. */
  flag_enabled: boolean;
}

export interface ConsoleGroup {
  group_key: string;
  audience: 'customer' | 'supplier';
  organization_id: string | null;
  marketplace_listing_id: string | null;
  name: string;
  domain: string | null;
  org_type: ConsoleOrgType;
  msp_name: string | null;
  exposed_hosts: number;
  own_exposed_hosts: number;
  supplier_exposed_hosts: number;
  exposed_suppliers: number;
  actions: number;
  /** runtime_advisories state of the organisation; flag-off groups are not selectable. */
  flag_enabled: boolean;
  recipients: ConsoleRecipient[];
}

export interface AdvisoryConsole {
  advisory: RuntimeAdvisoryHeader;
  /** The org-targeted exposure flag that decides who can be emailed (runtime_advisories). */
  flag_key: string;
  wave: {
    wave_id: string;
    enqueued_at: string;
    total: number;
    checked: number;
    failed: number;
    pending: number;
    fresh_pct: number;
  } | null;
  affected: { orgs: number; supplier_listings: number; org_person_pairs: number };
  tiles: {
    recipients: number;
    not_emailed: number;
    sent: number;
    delivered: number;
    unopened: null;
    failed: number;
    opened: null;
    cta_clicked: number;
    actions: number;
    auto_closed: number;
    /** Organisations left out of every send because runtime_advisories is off for them. */
    flag_off_orgs: number;
  };
  groups: ConsoleGroup[];
}

export interface ListingRuntimeTechnology {
  supplier_domain_id: string;
  host: string;
  discovered: boolean;
  product: string;
  product_label: string;
  version: string | null;
  source: string;
  confidence: string;
  first_seen_at: string;
  last_seen_at: string;
  last_checked_at: string;
}

export interface ListingRuntimeFinding {
  finding_id: string;
  supplier_domain_id: string;
  host: string;
  product: string;
  observed_version: string | null;
  support_state: RuntimeSupportState;
  state_reason: string | null;
  badge: string;
  severity: 'medium' | 'high' | null;
  advisory_id: string;
  customer_summary: string;
  admin_detail: string | null;
  eol_date: string | null;
  last_checked_at: string;
}

export interface ListingRuntime {
  technologies: ListingRuntimeTechnology[];
  findings: ListingRuntimeFinding[];
}

export interface PublicRuntimeHost {
  host: string;
  product: string;
  product_label: string;
  observed_version: string | null;
  support_state: RuntimeSupportState;
  badge: string;
  heading: string;
  body: string;
  last_seen_at: string | null;
  last_checked_at: string | null;
}

export interface PublicRuntimeCheck {
  domain: string;
  found: boolean;
  listingName: string | null;
  claimable: boolean;
  hosts: PublicRuntimeHost[];
}

export interface AdvisoryEmailPreview {
  subject: string;
  html: string;
  text: string;
  ctaUrl: string;
  ctaLabel: string;
  kind: string;
  hostCount: number;
}

/** ENT-6192 (D2): the reader's relationship to one recipient group, derived server-side. */
export type AdvisoryRelationship = 'own' | 'customer' | 'supplier';

/** ENT-6192 (D3): a group a send today would skip: runtime_advisories off, or no organisation to switch it on. */
export interface AdvisorySkippedGroup {
  name: string;
  reason: 'flag_off' | 'no_organisation';
}

/** ENT-6192 (D1/D3): the super-admin preview response of the advisory-email Edge function. */
export interface AdvisoryEmailPreviewResult {
  email: AdvisoryEmailPreview;
  /** Where the email's button goes (no token attached); opened in a new tab, never in the frame. */
  cta_url: string;
  skipped_groups: AdvisorySkippedGroup[];
  /** How many groups the preview rendered (flag on and off). */
  group_count: number;
}

/** ENT-6192 (D5): one exposed host row of the console host fold (get_advisory_console_hosts). */
export interface ConsoleHostRow {
  finding_id: string;
  host: string;
  supplier_name: string | null;
  is_own: boolean;
  observed_version: string | null;
  badge: string;
  support_state: RuntimeSupportState;
  state_reason: string | null;
  last_seen_at: string | null;
  last_checked_at: string | null;
}

export interface AdvisorySendOutcome {
  queued: number;
  failed: number;
  unchanged: number;
  /** C-9: targets the database skipped, with a reason; absent from older responses. */
  skipped?: Array<{ user_id: string; audience: string; reason: string }>;
  /** C-3: true when the Edge stopped at its time budget; `remaining` claimed rows are re-sendable. */
  partial?: boolean;
  remaining?: number;
}

/** The secondary line of a send/resend toast: everything that did not simply go out. */
export function describeSendOutcome(outcome: AdvisorySendOutcome): string | undefined {
  const skipped = outcome.skipped?.length ?? 0;
  const parts = [
    outcome.unchanged > 0 ? `${outcome.unchanged} already sent` : null,
    outcome.failed > 0 ? `${outcome.failed} could not be queued` : null,
    skipped > 0 ? `${skipped} skipped` : null,
    outcome.partial ? `${outcome.remaining ?? 0} not sent yet (time limit): send again to finish` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export interface AdvisoryDryRun {
  rows: Array<Record<string, string | number>>;
  pairs: number;
  distinct_people: number;
  csv: string;
}

/** Readable copy for the server's canonical advisory refusals. Unknown codes stay generic. */
const ADVISORY_ERROR_MESSAGES: Record<string, string> = {
  advisory_authentication_required: 'Sign in again to continue.',
  advisory_forbidden: "You don't have access to this company's advisories.",
  advisory_super_admin_required: 'Only Supply Guard administrators can do this.',
  advisory_not_found: 'This advisory could not be found.',
  advisory_not_active: 'This advisory is not active.',
  advisory_subscription_required: 'Actions need an active subscription for this organisation.',
  advisory_finding_not_in_scope: 'One of the selected hosts is not part of this organisation.',
  advisory_finding_already_patched: 'One of the selected hosts already reports the fix.',
  advisory_finding_not_exposed: 'One of the selected hosts changed since the page loaded. Refresh and review the selection.',
  advisory_invalid_domain: 'Enter a domain such as example.com.',
  advisory_domain_limit_reached: 'This organisation already has three confirmed website domains.',
  advisory_recipient_not_in_list: 'That recipient is not on this advisory\'s list, or every company they receive for has advisories switched off.',
  advisory_group_key_invalid: 'That company or listing reference is not valid. Refresh the console and try again.',
  advisory_group_not_found: 'That company or listing no longer exists. Refresh the console.',
  advisory_preview_payload_incomplete: 'The preview could not confirm which companies a send would skip. Try again.',
  advisory_primary_host_not_dismissable: 'The primary website cannot be marked as not yours.',
  advisory_host_not_found: 'That host no longer exists.',
  advisory_recheck_incomplete: 'The re-check is still running; wait for 95% or send with stale rows.',
  advisory_empty_selection: 'Select at least one recipient.',
  advisory_invalid_selection: 'The selection is not valid.',
  advisory_resend_not_failed: 'Only failed emails can be resent with "Resend failed".',
  advisory_email_tracking_unavailable: 'Email opens and provider clicks are not collected for privacy. Select recipients explicitly to resend.',
  advisory_resend_not_sent: 'That recipient has not been emailed yet.',
  advisory_feature_disabled: 'Advisories are not switched on for this organisation.',
  advisory_domain_not_owned: 'Confirm a domain you own: your verified business email must be at that domain.',
  advisory_due_date_in_past: 'Choose a due date that is today or later.',
  advisory_selection_too_large: 'Select fewer hosts and try again.',
};

export class RuntimeAdvisoryError extends Error {
  readonly code: string | null;
  constructor(code: string | null, message: string) {
    super(message);
    this.name = 'RuntimeAdvisoryError';
    this.code = code;
  }
}

export function advisoryErrorFromRpc(error: { message?: string | null; code?: string | null; details?: string | null } | null): RuntimeAdvisoryError {
  const raw = (error?.message ?? '').trim();
  const code = /^advisory_[a-z_]+$/.test(raw) ? raw : null;
  if (code === 'advisory_forbidden' && error?.details === 'host_not_on_profile') {
    return new RuntimeAdvisoryError(code, 'That host belongs to a different supplier profile. Open that profile to act on it.');
  }
  return new RuntimeAdvisoryError(code, (code && ADVISORY_ERROR_MESSAGES[code]) || 'Something went wrong. Try again.');
}

export function advisoryErrorMessage(error: unknown): string {
  if (error instanceof RuntimeAdvisoryError) return error.message;
  const code = (error as { errorCode?: unknown; details?: { errorCode?: unknown } } | null)?.details?.errorCode
    ?? (error as { errorCode?: unknown } | null)?.errorCode;
  if (typeof code === 'string' && ADVISORY_ERROR_MESSAGES[code]) return ADVISORY_ERROR_MESSAGES[code];
  return error instanceof Error && error.message ? error.message : 'Something went wrong. Try again.';
}

type UntypedRpc = (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string; code?: string; details?: string } | null }>;

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await (supabase.rpc as unknown as UntypedRpc)(fn, args);
  if (error !== null) throw advisoryErrorFromRpc(error);
  return data as T;
}

export function getAdvisoryPage(advisoryId: string, organizationId: string) {
  return rpc<AdvisoryPage>('get_advisory_page', {
    p_advisory_id: advisoryId,
    p_organization_id: organizationId,
  });
}

export function getVendorAdvisoryPage(advisoryId: string, vendorProfileId: string) {
  return rpc<VendorAdvisoryPage>('get_vendor_advisory_page', { p_advisory_id: advisoryId, p_vendor_profile_id: vendorProfileId });
}

export interface AdvisoryEmailContext {
  kind: 'supplier' | 'customer' | 'msp';
  resource_id: string;
  name: string;
  domain: string | null;
  organization_id: string | null;
  msp_home_organization_id?: string | null;
  enabled: boolean;
}

export interface AdvisoryEmailContexts {
  contexts: AdvisoryEmailContext[];
  has_unavailable_contexts: boolean;
}

export async function resolveAdvisoryEmailContext(advisoryId: string, token: string): Promise<AdvisoryEmailContexts> {
  const data = await rpc<AdvisoryEmailContexts>('resolve_advisory_email_context', { p_advisory_id: advisoryId, p_token: token });
  if (!data || !Array.isArray(data.contexts) || data.contexts.length === 0
    || typeof data.has_unavailable_contexts !== 'boolean'
    || data.contexts.some((context) => !context || !['supplier', 'customer', 'msp'].includes(context.kind)
      || typeof context.resource_id !== 'string' || !context.resource_id
      || typeof context.name !== 'string' || !context.name
      || typeof context.enabled !== 'boolean'
      || (context.domain !== null && typeof context.domain !== 'string')
      || (context.organization_id !== null && typeof context.organization_id !== 'string')
      || (context.msp_home_organization_id != null && (typeof context.msp_home_organization_id !== 'string'
        || !context.msp_home_organization_id || context.kind !== 'supplier' || !context.organization_id))
      || (context.kind !== 'supplier' && context.organization_id !== context.resource_id))) {
    throw new RuntimeAdvisoryError(null, 'Could not resolve this email’s companies. Try again.');
  }
  return data;
}

export function createPoamFromFindings(input: {
  advisoryId: string;
  selected: AdvisorySelection[];
  deferred?: AdvisorySelection[];
  dueDate?: string | null;
  shareWithSupplier?: boolean;
}) {
  return rpc<CreatePoamFromFindingsResult>('create_poam_from_findings', {
    p_advisory_id: input.advisoryId,
    p_selected: input.selected,
    p_deferred: input.deferred ?? [],
    p_due_date: input.dueDate ?? null,
    p_share_with_supplier: input.shareWithSupplier ?? true,
  });
}

export function setAdvisoryRowDecision(input: {
  advisoryId: string;
  organizationId: string;
  findingIds: string[];
  deferred: boolean;
}) {
  return rpc<number>('set_advisory_row_decision', {
    p_advisory_id: input.advisoryId,
    p_organization_id: input.organizationId,
    p_finding_ids: input.findingIds,
    p_deferred: input.deferred,
  });
}

export function confirmOrgWebsiteDomain(organizationId: string, domain: string) {
  return rpc<{ id: string; normalized_domain: string }>('confirm_org_website_domain', {
    p_organization_id: organizationId,
    p_domain: domain,
  });
}

export const ADVISORY_CTA_TOKEN_RE = /^[0-9a-f]{64}$/;

export function recordAdvisoryCtaVisit(advisoryId: string, token: string, organizationId: string | null) {
  return rpc<{ recorded: boolean; reason: string }>('record_advisory_cta_visit', {
    p_advisory_id: advisoryId,
    p_token: token,
    p_organization_id: organizationId,
  });
}

/** Delays between CTA-visit attempts (bounded: three attempts in all). */
export const CTA_VISIT_RETRY_DELAYS_MS = [1_000, 4_000];

/**
 * The CTA visit with a bounded retry (fail-closed-on-unknown; PR #3654 review). The token has
 * already left the URL (E-03), so it is held in memory here: a transient failure (network, an
 * unknown server error) is retried; a canonical refusal (`advisory_*`, e.g. a token bound to
 * someone else) is final. The visit RPC is idempotent (a replay is reported, never double-counted).
 * Resolves with the server answer, or rejects with the last error after the final attempt so the
 * caller can report it.
 */
export async function recordAdvisoryCtaVisitWithRetry(
  advisoryId: string,
  token: string,
  organizationId: string | null,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
) {
  let lastError: unknown;
  for (let attempt = 0; attempt <= CTA_VISIT_RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) await wait(CTA_VISIT_RETRY_DELAYS_MS[attempt - 1]);
    try {
      return await recordAdvisoryCtaVisit(advisoryId, token, organizationId);
    } catch (error) {
      lastError = error;
      if (error instanceof RuntimeAdvisoryError && error.code?.startsWith('advisory_')) throw error;
    }
  }
  throw lastError;
}

// ---------------------------------------------------------------------------
// v1.12 index + read state, bulk preview, supplier "not ours" (20261022020358)
// ---------------------------------------------------------------------------

export interface AdvisoryIndexEntry extends RuntimeAdvisoryHeader {
  exposed_hosts: number;
  own_exposed_hosts: number;
  supplier_exposed_hosts: number;
  patched_hosts: number;
  open_actions: number;
  organizations: number;
  read_at: string | null;
  new_hosts_since_read: number;
  unread: boolean;
  all_patched: boolean;
}

export function listMyAdvisories(organizationId: string) {
  return rpc<{ advisories: AdvisoryIndexEntry[] }>('list_my_advisories', { p_organization_id: organizationId });
}

export function listVendorAdvisories(vendorProfileId: string) {
  return rpc<{ advisories: AdvisoryIndexEntry[] }>('list_vendor_advisories', { p_vendor_profile_id: vendorProfileId });
}

export interface VendorAdvisoriesShell {
  /** True when the open profile's claimant organisation has the flag on. */
  enabled: boolean;
  /** The caller's unread supplier advisories (0 when disabled). */
  unread: number;
  /** The server-owned rollout label, present only while this authorised shell is enabled. */
  release_stage?: 'new' | 'alpha' | 'beta' | null;
}

/** ENT-5889 (v1.12): supplier-shell Advisories exposure + unread badge (get_vendor_advisories_shell). */
export function getVendorAdvisoriesShell(vendorProfileId: string) {
  return rpc<VendorAdvisoriesShell>('get_vendor_advisories_shell', { p_vendor_profile_id: vendorProfileId });
}

export type AdvisoriesShell = VendorAdvisoriesShell;

/** ENT-6091: active-organisation exposure and unread count, authorised by the server. */
export function getAdvisoriesShell(organizationId: string) {
  return rpc<AdvisoriesShell>('get_advisories_shell', { p_organization_id: organizationId });
}

/** ENT-6091: only the active MSP's authorised, flagged managed customers. */
export function getMspAdvisoryPage(advisoryId: string, mspOrganizationId: string) {
  return rpc<AdvisoryPage>('get_msp_advisory_page', { p_advisory_id: advisoryId, p_msp_organization_id: mspOrganizationId });
}

export function listMspAdvisories(mspOrganizationId: string) {
  return rpc<{ advisories: AdvisoryIndexEntry[] }>('list_msp_advisories', { p_msp_organization_id: mspOrganizationId });
}

export function getMspAdvisoriesShell(mspOrganizationId: string) {
  return rpc<AdvisoriesShell>('get_msp_advisories_shell', { p_msp_organization_id: mspOrganizationId });
}

export function markAdvisoryRead(advisoryId: string) {
  return rpc<{ advisory_id: string; read_at: string; last_seen_host_count: number }>('mark_advisory_read', {
    p_advisory_id: advisoryId,
  });
}

export interface PoamPreviewAction {
  organization_id: string;
  is_own: boolean;
  supplier_name: string | null;
  hosts: string[];
  title: string;
  severity: 'medium' | 'high';
  risk_statement: string;
  recommended_action: string;
  due_date: string;
  shared_with_supplier: boolean;
}

export function previewPoamFromFindings(advisoryId: string, selected: AdvisorySelection[]) {
  return rpc<{ actions: PoamPreviewAction[] }>('preview_poam_from_findings', {
    p_advisory_id: advisoryId,
    p_selected: selected,
  });
}

export function dismissRuntimeHostNotOurs(supplierDomainId: string, reasonDetail: string | null, vendorProfileId: string) {
  return rpc<{ dismissal_id: string; domain: string }>('dismiss_runtime_host_not_ours', {
    p_supplier_domain_id: supplierDomainId,
    p_reason_detail: reasonDetail,
    p_vendor_profile_id: vendorProfileId,
  });
}

export function getAdvisoryConsole(advisoryId: string) {
  return rpc<AdvisoryConsole>('get_advisory_console', { p_advisory_id: advisoryId });
}

export function getAdvisoryConsoleHosts(advisoryId: string, groupKey: string) {
  return rpc<ConsoleHostRow[]>('get_advisory_console_hosts', { p_advisory_id: advisoryId, p_group_key: groupKey });
}

export function getListingRuntimeTechnologies(listingId: string) {
  return rpc<ListingRuntime>('get_listing_runtime_technologies', { p_listing_id: listingId });
}

async function invokeAdvisoryEmail<T>(body: Record<string, unknown>, fallback: string): Promise<T> {
  const { data, error, response } = await invokeProtectedFunction<T>('advisory-email', body);
  if (error || !data) {
    throw await buildFunctionInvokeError('advisory-email', error, fallback, response);
  }
  return data;
}

export function previewAdvisoryEmail(advisoryId: string, userId: string, audience: 'customer' | 'supplier') {
  return invokeAdvisoryEmail<AdvisoryEmailPreviewResult>(
    { action: 'preview', advisory_id: advisoryId, user_id: userId, audience },
    'The email preview could not be rendered',
  );
}

export function dryRunAdvisory(advisoryId: string) {
  return invokeAdvisoryEmail<AdvisoryDryRun>(
    { action: 'dry_run', advisory_id: advisoryId },
    'The dry run could not be produced',
  );
}

export type AdvisoryRecipientRef = { user_id: string; audience: 'customer' | 'supplier' };

export function sendAdvisory(advisoryId: string, recipients: AdvisoryRecipientRef[], allowStale: boolean) {
  return invokeAdvisoryEmail<AdvisorySendOutcome>(
    { action: 'send', advisory_id: advisoryId, recipients, allow_stale: allowStale },
    'The advisory could not be sent',
  );
}

export function resendAdvisory(
  advisoryId: string,
  recipients: AdvisoryRecipientRef[],
  reason: 'failed' | 'unopened' | 'selected',
) {
  return invokeAdvisoryEmail<AdvisorySendOutcome>(
    { action: 'resend', advisory_id: advisoryId, recipients, reason },
    'The advisory could not be resent',
  );
}

export function sendAdvisoryForMspOrgs(advisoryId: string, organizationIds: string[]) {
  return invokeAdvisoryEmail<AdvisorySendOutcome>(
    { action: 'msp_send', advisory_id: advisoryId, organization_ids: organizationIds },
    'The organisations could not be emailed',
  );
}

export class PublicRuntimeCheckRateLimited extends Error {
  constructor() {
    super('Too many checks from this connection. Try again in a few minutes.');
    this.name = 'PublicRuntimeCheckRateLimited';
  }
}

export class PublicRuntimeCheckUnavailable extends Error {
  constructor() {
    super('Domain checks are not available yet.');
    this.name = 'PublicRuntimeCheckUnavailable';
  }
}

async function readInvokeErrorCode(error: unknown, response: Response | undefined): Promise<string | null> {
  const source = response ?? (error as { context?: Response } | null)?.context;
  if (!source || typeof (source as Response).clone !== 'function') return null;
  try {
    const body = await (source as Response).clone().json() as { errorCode?: unknown };
    return typeof body.errorCode === 'string' ? body.errorCode : null;
  } catch {
    return null;
  }
}

export async function checkPublicRuntimeDomain(domain: string): Promise<PublicRuntimeCheck> {
  const { data, error, response } = await supabase.functions.invoke<PublicRuntimeCheck>('public-runtime-check', {
    body: { domain },
  });
  const status = response?.status ?? (error as { context?: { status?: number } } | null)?.context?.status;
  if (status === 429) throw new PublicRuntimeCheckRateLimited();
  // Exposure control: the platform runtime_advisories flag is off, so the page does not exist.
  if (status === 404 && (await readInvokeErrorCode(error, response)) === 'feature_disabled') {
    throw new PublicRuntimeCheckUnavailable();
  }
  if (error || !data) {
    throw await buildFunctionInvokeError('public-runtime-check', error, 'The domain could not be checked', response);
  }
  return data;
}

// ---------------------------------------------------------------------------
// CSV (pure): serialise server rows exactly as returned.
// ---------------------------------------------------------------------------

export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  const s = typeof value === 'string' ? value : String(value);
  // Neutralise spreadsheet formula injection (C-12) in text cells, then quote when needed.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

export function rowsToCsv<T extends object>(rows: T[], columns: Array<{ key: keyof T; header: string }>): string {
  const lines = [columns.map((c) => csvCell(c.header)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((c) => csvCell(row[c.key])).join(','));
  }
  return `${lines.join('\n')}\n`;
}

export const ADVISORY_ROW_CSV_COLUMNS: Array<{ key: keyof AdvisoryRow; header: string }> = [
  { key: 'supplier_name', header: 'Supplier' },
  { key: 'host', header: 'Host' },
  { key: 'discovered', header: 'Found by discovery' },
  { key: 'product', header: 'Product' },
  { key: 'observed_version', header: 'Reported version' },
  { key: 'badge', header: 'Status' },
  { key: 'reference_version', header: 'Fix version' },
  { key: 'last_checked_at', header: 'Checked' },
  { key: 'customer_summary', header: 'Summary' },
  { key: 'poam_status', header: 'Action status' },
  { key: 'deferred_at', header: 'Deferred at' },
];

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Selected exposed supplier rows that would be sent to create_poam_from_findings. */
export function bulkSelectedRows(rows: AdvisoryRow[], selected: ReadonlySet<string>): AdvisoryRow[] {
  return rows.filter((row) =>
    !row.is_own && isExposed(row) && !hasOpenAction(row) && selected.has(rowKey(row))
  );
}

/** The bulk count: distinct suppliers among selected exposed supplier rows with no open action. */
export function countBulkActions(rows: AdvisoryRow[], selected: ReadonlySet<string>): number {
  return new Set(bulkSelectedRows(rows, selected).map((row) => `${row.organization_id}:${row.marketplace_listing_id}`)).size;
}

export function rowKey(row: Pick<AdvisoryRow, 'organization_id' | 'finding_id'>): string {
  return `${row.organization_id}:${row.finding_id}`;
}

export function isExposed(row: Pick<AdvisoryRow, 'support_state'>): boolean {
  return row.support_state === 'below_fix' || row.support_state === 'eol';
}

export function hasOpenAction(row: Pick<AdvisoryRow, 'poam_id' | 'poam_status'>): boolean {
  return Boolean(row.poam_id) && row.poam_status !== 'resolved' && row.poam_status !== 'rejected';
}

export function formatAdvisoryDate(value: string | null | undefined): string {
  if (!value) return '—';
  const calendarOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = new Date(calendarOnly ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', ...(calendarOnly ? { timeZone: 'UTC' } : {}) });
}

export function formatAdvisoryDateTime(value: string | null | undefined): string {
  return formatDateTime(value);
}

export type BadgeTone = 'warn' | 'bad' | 'ok' | 'benign';

/** Visual tone for a SERVER badge string (the badge text itself is never derived here). */
export function badgeTone(badge: string): BadgeTone {
  if (badge === 'Act now') return 'bad';
  if (badge === 'Update needed' || badge === 'Unsupported') return 'warn';
  if (badge === 'Patched') return 'ok';
  return 'benign';
}

/** ENT-6010: one row of Admin › Content › Advisories (the advisory catalogue; drafts are visible to super admins by RLS). */
export interface AdminAdvisoryListEntry {
  advisory_id: string;
  title: string;
  product: string;
  cve_ids: string[];
  status: 'draft' | 'active' | 'retired';
  exploited: boolean;
  published_at: string;
}

type UntypedFrom = (table: string) => {
  select: (columns: string, options: { count: 'exact' }) => {
    order: (column: string, options: { ascending: boolean }) => {
      order: (column: string, options: { ascending: boolean }) => {
        range: (from: number, to: number) => Promise<{
          data: unknown;
          count: number | null;
          error: { message?: string | null; code?: string | null; details?: string | null } | null;
        }>;
      };
    };
  };
};

/** PostgREST returns at most this many rows per request (the server's max-rows cap). */
const ADMIN_ADVISORY_PAGE_SIZE = 1000;

/**
 * ENT-6010: the super-admin advisory list; fails closed (a read error throws, never an empty list).
 * Reads every page against an exact count, so the server's row cap can never truncate it silently.
 */
export async function listAdminAdvisories(): Promise<AdminAdvisoryListEntry[]> {
  const rows: AdminAdvisoryListEntry[] = [];
  for (let from = 0; ; from += ADMIN_ADVISORY_PAGE_SIZE) {
    const { data, count, error } = await (supabase.from as unknown as UntypedFrom)('runtime_advisories')
      .select('advisory_id, title, product, cve_ids, status, exploited, published_at', { count: 'exact' })
      .order('published_at', { ascending: false })
      .order('advisory_id', { ascending: true })
      .range(from, from + ADMIN_ADVISORY_PAGE_SIZE - 1);
    if (error !== null) throw advisoryErrorFromRpc(error);
    // Fail closed: a success-shaped response without a row array or a count is unknown, never "no advisories".
    if (!Array.isArray(data) || typeof count !== 'number') {
      throw new RuntimeAdvisoryError('advisory_unexpected_response', 'The advisory list response was not a list.');
    }
    rows.push(...(data as AdminAdvisoryListEntry[]));
    if (rows.length >= count) return rows;
    if (data.length === 0) {
      throw new RuntimeAdvisoryError('advisory_unexpected_response', 'The advisory list ended before its reported count.');
    }
  }
}
